import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Button, DatePicker, Form, Modal, Select, Space, Table } from 'antd';
import { Catcher } from '../../../';
import dayjs, { Dayjs } from 'dayjs';
import { ICardSetReassignRow } from '../../../../interfaces';
import {
    useCardSetFutureAssignments,
    useCardSetInfo,
    useCardSetManageCardSetsOptions,
    useCardSetManageChecks,
    useCardSetManageChildrenSelect,
    useCardSetParentAssignments,
    useUiIsSpinnerFound,
} from '../../../../hooks';
import {
    CARD_SET_MANAGE_NEW_CARD_SET,
    getCardSetManageCardSetsOptions,
    sendCardSetCancelAll,
    sendCardSetReassignAll,
} from '../../../../middleware';
import { disablePreviousDates } from '../../../../instruments';
import { ITrNotificationMessage, tB, tF, tI, tNM, tT, tV } from '../../../../translate';

interface IReassignAllForm {
    onDone: () => void;
}

export const ReassignAllForm: React.FC<IReassignAllForm> = observer(({ onDone }: IReassignAllForm) => {
    const { childrenCount, cardsCount, assignedCompaniesCount } = useCardSetManageChecks();
    const { total: futureCount, isLoaded: isFutureLoaded } = useCardSetFutureAssignments();
    const { total: parentCount, isLoaded: isParentLoaded } = useCardSetParentAssignments();
    const reassignDisabledReason: ITrNotificationMessage | undefined = !childrenCount
        ? 'Company has no child companies'
        : !cardsCount
        ? 'Card set has no cards'
        : !isFutureLoaded || !isParentLoaded
        ? 'Blocking assignments are not loaded'
        : parentCount
        ? 'Card set has parent assignments'
        : futureCount
        ? 'Card set has future assignments'
        : undefined;
    const cancelDisabledReason: ITrNotificationMessage | undefined =
        reassignDisabledReason || (!assignedCompaniesCount ? 'Cards are not assigned to companies' : undefined);

    const [form] = Form.useForm<ICardSetReassignRow>();
    const [isSending, setIsSending] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);
    const { id, companyId: cardSetCompanyId = '' } = useCardSetInfo();
    const companyId = Form.useWatch('companyId', form);
    const childrenCompanies = useCardSetManageChildrenSelect();
    const cardSets = useCardSetManageCardSetsOptions(companyId);
    const isCardSetsLoading = useUiIsSpinnerFound(['GET_CARD_SETS_SELECT']);
    const requiredRules = [{ required: true, message: tV('Cannot be Empty.') }];
    const isBusy = isSending || isCanceling;
    const isDisabled = !!reassignDisabledReason || isBusy;
    const [nowValue] = useState<Dayjs>(() => dayjs());

    const companyOptions = childrenCompanies.map((c) => ({ value: c.id, label: c.name }));
    const cardSetOptions = [
        { value: CARD_SET_MANAGE_NEW_CARD_SET, label: tF('New card set') },
        ...cardSets.map((c) => ({ value: c.id, label: c.name })),
    ];

    const onCompanyChange = (value: string): void => {
        form.setFieldValue('targetCardSetId', undefined);
        value && getCardSetManageCardSetsOptions(value);
    };

    const submit = async (): Promise<void> => {
        let row: ICardSetReassignRow;
        try {
            row = await form.validateFields();
        } catch {
            // Field errors are shown by the form itself
            return;
        }
        setIsSending(true);
        try {
            const { isError } = await sendCardSetReassignAll(id, row, row.startDate === nowValue);
            if (!isError) {
                form.resetFields();
                onDone();
            }
        } finally {
            setIsSending(false);
        }
    };

    const cancelAll = (): void => {
        Modal.confirm({
            title: tT('Are you sure?'),
            content: tT('All cards will be returned to the card set company.'),
            okText: tB('Yes'),
            cancelText: tB('No'),
            wrapClassName: 'modal',
            onOk: async () => {
                setIsCanceling(true);
                try {
                    const { isError } = await sendCardSetCancelAll(id, cardSetCompanyId);
                    !isError && onDone();
                } finally {
                    setIsCanceling(false);
                }
            },
        });
    };

    const tableColumns = [
        { title: tF('Company'), dataIndex: 'company' },
        { title: tF('Start Date'), dataIndex: 'startDate', width: 220 },
        { title: tF('Target Company Card Set'), dataIndex: 'targetCardSet' },
    ];

    const dataSource = [
        {
            key: 'reassign',
            company: (
                <Form.Item name={'companyId'} rules={requiredRules}>
                    <Select options={companyOptions} onChange={onCompanyChange} showSearch optionFilterProp={'label'} />
                </Form.Item>
            ),
            startDate: (
                <Form.Item name={'startDate'} rules={requiredRules}>
                    <DatePicker disabledDate={disablePreviousDates} presets={[{ label: tI('Now'), value: nowValue }]} />
                </Form.Item>
            ),
            targetCardSet: (
                <Form.Item name={'targetCardSetId'}>
                    <Select options={cardSetOptions} allowClear loading={isCardSetsLoading} disabled={!companyId} />
                </Form.Item>
            ),
        },
    ];

    return (
        <Catcher>
            <Form form={form} component={false} disabled={isDisabled}>
                <Table
                    dataSource={dataSource}
                    columns={tableColumns}
                    pagination={false}
                    className={'table-theme sub-table'}
                    size={'small'}
                />
            </Form>
            <Space>
                <Button
                    type={'primary'}
                    disabled={!!reassignDisabledReason || isCanceling}
                    loading={isSending}
                    title={reassignDisabledReason ? tNM(reassignDisabledReason) : undefined}
                    onClick={submit}
                >
                    {tB('Reassign All')}
                </Button>
                <Button
                    danger
                    disabled={!!cancelDisabledReason || isSending}
                    loading={isCanceling}
                    title={cancelDisabledReason ? tNM(cancelDisabledReason) : undefined}
                    onClick={cancelAll}
                >
                    {tB('Cancel All')}
                </Button>
            </Space>
        </Catcher>
    );
});
